<script lang="ts" setup>
import { PropType, computed, useSlots } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import type { BreadcrumbItem } from 'vuetify/lib/components/VBreadcrumbs/VBreadcrumbs.mjs'

import BackButton from '@/components/BackButton.vue'

const slots = useSlots()

const skeletonPresets = [
  'table',
  'article',
  'list-item@5',
  'image, text',
] as const
type SkeletonPreset = (typeof skeletonPresets)[number]

const {
  title,
  mobileFab,
  isMobile,
  onlyWhite,
  progress,
  items,
  backButtonTo,
  contextualNav,
  mobileFooter,
  color,
  publicMode,
  loading,
  skeletonType,
  footerReserveHeight,
  titleColor,
  titleWeight,
  titleSize,
  bgWhite,
  subheaderFullBleed,
  header,
  constrainWidth,
  maxWidth,
  contentTopPadding,
} = defineProps({
  title: { type: String, required: false, default: null },
  mobileFab: { type: Boolean, default: false },
  isMobile: { type: Boolean, default: false },
  onlyWhite: { type: Boolean, default: false },
  progress: { type: Number, default: null },
  items: { type: Array as PropType<BreadcrumbItem[]>, default: () => [] },
  backButtonTo: { type: Object as PropType<RouteLocationRaw>, default: null },
  bgWhite: { type: Boolean, default: false },
  contextualNav: { type: Boolean, default: false },
  mobileFooter: { type: Boolean, default: false },
  color: { type: String, default: null, required: false },
  publicMode: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  isMembers: { type: Boolean, default: false, required: false },
  skeletonType: {
    type: String as PropType<SkeletonPreset | string>,
    default: 'table',
  },
  footerReserveHeight: { type: Number, default: 96 },
  titleColor: { type: String, default: 'text-green' },
  titleWeight: { type: String, default: 'font-weight-bold' },
  titleSize: { type: String, default: 'text-h6' },
  subheaderFullBleed: { type: Boolean, default: false },
  header: { type: Boolean, default: true },

  constrainWidth: { type: Boolean, default: true },
  maxWidth: {
    type: [Number, String] as PropType<number | string>,
    default: 1200,
  },
  contentTopPadding: {
    type: String as PropType<'auto' | 'none' | 'subheader' | 'default'>,
    default: 'auto',
  },
})

const contentBgClass = computed(() =>
  bgWhite || onlyWhite || isMobile ? 'bg-white' : 'bg-grey-lighten-5'
)

const headerColor = computed(() => {
  if (color) return 'transparent'
  if (bgWhite) return 'white'
  return !isMobile ? 'grey-lighten-5' : 'white'
})

const topPadStyle = computed((): Record<string, string> => {
  if (isMobile) return { paddingTop: '0px' }

  if (contentTopPadding === 'none') return { paddingTop: '0px' }
  if (contentTopPadding === 'subheader') return { paddingTop: '64px' }
  if (contentTopPadding === 'default') return { paddingTop: '24px' }

  if (slots.subheader) return { paddingTop: '64px' }
  return { paddingTop: '48px' }
})

const normalizeMaxWidth = (value: number | string): string => {
  if (typeof value === 'number') return `${value}px`
  const trimmed = value.trim()
  if (/^\d+$/.test(trimmed)) return `${trimmed}px`
  return trimmed
}

const resolvedMaxWidth = computed(() =>
  constrainWidth ? normalizeMaxWidth(maxWidth) : 'none'
)

const contentWrapStyle = computed<Record<string, string>>(() => ({
  maxWidth: resolvedMaxWidth.value,
}))

const contentWrapClass = computed(() => (constrainWidth ? 'mx-md-auto' : ''))

const subheaderInnerStyle = computed<Record<string, string>>(() => ({
  maxWidth: subheaderFullBleed ? 'none' : resolvedMaxWidth.value,
}))

const showMobileTitlebar = computed(
  () => contextualNav && isMobile && !!title && header
)
</script>

<template>
  <v-app>
    <v-main
      :style="{
        marginTop: contextualNav ? '64px' : '',
      }"
    >
      <v-sheet
        v-if="header && !(contextualNav && isMobile)"
        class="d-flex align-center position-relative"
        :class="contextualNav ? 'border-b-thin bg-white' : ''"
        :color="headerColor"
        :height="48"
      >
        <div v-if="color" class="header-color-abs">
          <slot name="header-color" />
        </div>

        <div
          class="w-100 h-100 d-flex align-center justify-space-between header-content px-4"
        >
          <!-- Esquerda -->
          <div class="d-flex align-center min-w-0 flex-1">
            <template v-if="title || isMobile">
              <BackButton
                v-if="!publicMode && (isMobile || backButtonTo)"
                :to="backButtonTo"
              />

              <h2
                v-if="isMobile"
                class="title-ellipsis text-subtitle-1 m-0 ml-2"
              >
                {{ title }}
              </h2>

              <div v-else-if="title" class="d-flex align-center ga-3 ml-6">
                <span :class="[titleColor, titleWeight, titleSize]">
                  {{ title }}
                </span>
                <slot name="header-right" />
              </div>
            </template>

            <template v-else-if="items.length && !isMobile">
              <v-breadcrumbs
                :items="items"
                divider=">"
                class="eve-breadcrumbs text-grey-darken-1"
              >
                <template #title="{ item, index }">
                  <span
                    :class="
                      index === items.length - 1
                        ? 'green-breadcrumbs'
                        : 'text-grey-darken-1'
                    "
                  >
                    {{ item.title }}
                  </span>
                </template>
              </v-breadcrumbs>
            </template>
          </div>

          <!-- Direita -->
          <div class="d-flex align-center ml-2">
            <div v-if="progress !== null" class="align-center w-100">
              <span class="font-weight-bold text-subtitle-1 mr-2"
                >{{ progress }}%</span
              >
              <span class="text-caption text-grey-darken-2 mr-6">
                {{ $t('events.status.ready_to_publish') }}
              </span>
            </div>
            <slot name="publish-button" />
          </div>
        </div>
      </v-sheet>

      <v-sheet
        v-if="showMobileTitlebar"
        class="mobile-titlebar"
        color="white"
        height="48"
      >
        <div class="mobile-titlebar__inner">
          <BackButton
            v-if="!publicMode && (isMobile || backButtonTo)"
            :to="backButtonTo"
            class="mobile-titlebar__back"
          />
          <h2 class="mobile-titlebar__title">
            {{ title }}
          </h2>
          <div class="mobile-titlebar__right">
            <slot name="header-right" />
            <slot name="publish-button" />
          </div>
        </div>
      </v-sheet>

      <div
        v-if="$slots.subheader"
        class="eve-subheader-wrap"
        :class="[
          { 'eve-subheader-full': subheaderFullBleed },
          { 'eve-subheader-mobile': isMobile },
          { 'eve-subheader-after-mobile-titlebar': showMobileTitlebar },
        ]"
      >
        <div
          class="eve-subheader-inner"
          :class="isMobile ? 'px-4' : 'px-md-4'"
          :style="subheaderInnerStyle"
        >
          <slot name="subheader" />
        </div>
      </div>

      <!-- Barra de progresso -->
      <div v-if="progress !== null" class="w-100">
        <v-progress-linear
          :model-value="progress"
          color="#8DC63F"
          height="3"
          background-color="#F1F5ED"
        />
      </div>

      <!-- CONTEÚDO -->
      <v-container
        fluid
        class="d-flex flex-column flex-grow-1 px-0 pb-0"
        :class="[contentBgClass]"
        :style="topPadStyle"
      >
        <div
          class="w-100 flex-grow"
          :class="[contentWrapClass, isMobile ? 'pa-0' : 'px-md-4']"
          :style="contentWrapStyle"
        >
          <transition name="fade-fast" mode="out-in">
            <div
              :key="loading ? 'skeleton' : 'content'"
              :role="loading ? 'status' : undefined"
              :aria-busy="loading ? 'true' : 'false'"
              aria-live="polite"
            >
              <template v-if="loading">
                <slot name="skeleton" :type="skeletonType">
                  <v-skeleton-loader
                    :type="skeletonType"
                    class="rounded-lg"
                    boilerplate
                  />
                </slot>
              </template>

              <template v-else>
                <slot />
              </template>
            </div>
          </transition>
        </div>

        <!-- Espaço para rodapé fixo -->
        <div
          v-if="$slots['footer-actions'] && (!isMobile || mobileFooter)"
          :style="{ height: `${footerReserveHeight}px` }"
        />

        <!-- Rodapé fixo -->
        <v-sheet
          v-if="$slots['footer-actions'] && (!isMobile || mobileFooter)"
          class="footer-actions-wrapper"
          :class="{ 'footer-actions-mobile': isMobile && mobileFooter }"
        >
          <slot name="footer-actions" />
        </v-sheet>

        <!-- FAB mobile -->
        <v-btn
          v-if="mobileFab && isMobile"
          icon="mdi-plus"
          color="primary"
          class="position-fixed z-2 elevation-2 bottom-14 right-10"
          aria-label="Criar"
        />
      </v-container>
    </v-main>
  </v-app>
</template>

<style scoped>
:deep(.v-sheet) {
  overflow: visible;
}

.header-color-abs {
  position: absolute;
  inset: 0 auto auto 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.header-content {
  position: relative;
  z-index: 1;
}

.title-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 72vw;
}

/* =========================
   MOBILE TITLEBAR (com sombra)
   ========================= */
.mobile-titlebar {
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  z-index: 5;
}

.mobile-titlebar__inner {
  height: 48px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px 0 0;
}

.mobile-titlebar__title {
  margin: 0;
  font-size: 18px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.mobile-titlebar__right {
  display: flex;
  align-items: center;
}

/* =========================
   FOOTER
   ========================= */
.footer-actions-wrapper {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  padding: 16px;
  border-top: 1px solid #e0e0e0;
  z-index: 100;
  display: flex;
  justify-content: center;
  gap: 16px;
}

.footer-actions-mobile {
  display: flex;
  padding-top: 24px;
  padding-bottom: 24px;
}

.flex-1 {
  flex: 1 1 auto;
}
.min-w-0 {
  min-width: 0;
}

/* =========================
   TRANSITION
   ========================= */
.fade-fast-enter-active,
.fade-fast-leave-active {
  transition: opacity 0.12s ease;
}
.fade-fast-enter-from,
.fade-fast-leave-to {
  opacity: 0;
}

/* =========================
   SUBHEADER (desktop default)
   ========================= */
.eve-subheader-wrap {
  width: 100%;
  height: 48px;
  margin-top: 14px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
}

.eve-subheader-inner {
  width: 100%;
  margin: 0;
  justify-content: flex-start;
}

.eve-subheader-full .eve-subheader-inner {
  max-width: none;
}

/* =========================
   SUBHEADER (mobile fix)
   ========================= */
.eve-subheader-mobile {
  height: auto;
  flex-direction: column;
}

.eve-subheader-mobile .eve-subheader-inner {
  display: flex;
  flex-direction: column;
}

.eve-subheader-after-mobile-titlebar {
  margin-top: 0 !important;
}

.eve-breadcrumbs .v-breadcrumbs-item--disabled {
  opacity: 1 !important;
}

.eve-breadcrumbs .green-breadcrumbs {
  opacity: 1 !important;
  color: var(--standart-theme---EVE-primary, #8dc63f) !important;
}

@media (max-width: 600px) {
  .eve-subheader-wrap {
    border-radius: 0 !important;
  }

  :deep(.v-main) {
    padding: 0 !important;
  }

  :deep(.v-application),
  :deep(.v-application__wrap),
  :deep(.v-main) {
    background: #fff !important;
  }
}
</style>
